/* SS product-card image slider — mirraw-style behaviour, no library.
 *
 * Mobile swipe is pure CSS scroll-snap; this file only adds what CSS can't do:
 *   - prev/next arrows + clickable dots (delegated, so it costs one listener for the whole page
 *     and works for cards injected later by pagination / infinite scroll / tab switches)
 *   - active-dot + is-start/is-end state from scroll position
 *   - mirraw's deferred loading: slide 1 ships real, slides 2..N ship as a 1x1 gif with the
 *     real URL parked in data-original; we swap it in n+1 ahead so the next slide is never blank.
 *
 * Controls are siblings of the card <a>, so clicks can't navigate; we still stop propagation
 * defensively in case a theme ever wraps the media box in another link.
 */
(function () {
  'use strict';

  var SEL = '.sspcs';

  function slidesOf(track) {
    return Array.prototype.slice.call(track.querySelectorAll('.sspcs__slide'));
  }

  function indexOf(track) {
    var w = track.clientWidth;
    if (!w) return 0;
    return Math.round(track.scrollLeft / w);
  }

  /* Swap a deferred slide's placeholder for the real image (mirraw's data-original trick). */
  function hydrate(slide) {
    if (!slide) return;
    var imgs = slide.querySelectorAll('img[data-original]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var real = img.getAttribute('data-original');
      if (!real) continue;
      var srcset = img.getAttribute('data-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', real);
      img.removeAttribute('data-original');
      img.removeAttribute('data-srcset');
      img.classList.remove('sspcs-deferred');
    }
  }

  /* n+1 prefetch: keep the current slide and the one after it loaded. */
  function hydrateAround(track, i) {
    var s = slidesOf(track);
    hydrate(s[i]);
    hydrate(s[i + 1]);
  }

  function sync(track) {
    var wrap = track.closest('.sspcs-wrap');
    if (!wrap) return;
    var s = slidesOf(track);
    var i = Math.max(0, Math.min(indexOf(track), s.length - 1));

    var dots = wrap.querySelectorAll('.sspcs__dot');
    for (var d = 0; d < dots.length; d++) {
      dots[d].classList.toggle('is-active', d === i);
      dots[d].setAttribute('aria-current', d === i ? 'true' : 'false');
    }
    wrap.classList.toggle('is-start', i <= 0);
    wrap.classList.toggle('is-end', i >= s.length - 1);
    hydrateAround(track, i);
  }

  function goTo(track, i, instant) {
    var s = slidesOf(track);
    i = Math.max(0, Math.min(i, s.length - 1));
    hydrateAround(track, i); // load before we move, so the slide is never blank
    // Scrub passes instant: a smooth scroll animates over ~300ms, so the image would lag
    // behind the cursor and land on whichever slide the mouse had already left.
    track.scrollTo({ left: i * track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
  }


  /* --- delegated events (survive pagination / infinite scroll / tab swaps) --- */

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('.sspcs__arrow, .sspcs__dot');
      if (!btn) return;
      var wrap = btn.closest('.sspcs-wrap');
      var track = wrap && wrap.querySelector(SEL);
      if (!track) return;

      e.preventDefault();
      e.stopPropagation();

      // Once the shopper picks a dot themselves, hover stops steering this card — resetting
      // to image 1 when they move the mouse away would undo their own navigation.
      wrap.dataset.sspcsManual = '1';

      if (btn.classList.contains('sspcs__dot')) {
        goTo(track, parseInt(btn.dataset.i, 10) || 0);
      } else {
        goTo(track, indexOf(track) + (btn.classList.contains('sspcs__arrow--next') ? 1 : -1));
      }
    },
    true // capture: beat the card <a> to the click
  );

  document.addEventListener(
    'scroll',
    function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('sspcs')) sync(t);
    },
    true // capture: scroll doesn't bubble
  );

  /* Desktop hover: show the SECOND image, and nothing more.
   *
   * This deliberately replaced a hover-scrub (cursor position picks the image, Zara-style).
   * It demoed badly — "sar sar image change hota hai": with five zones across a 306px card,
   * every ordinary mouse movement fires an image change, so the card flickers while you are
   * just moving across the grid. Scrub only works on much larger cards. Reverted to the one
   * thing the theme always did: hover shows image 2. Everything past that is the dots' job.
   *
   * The one improvement kept over the theme's original: this drives the SLIDER rather than
   * cross-fading a separate <img>, so from image 2 the shopper can carry on to 3, 4, 5 with
   * the dots instead of stopping at 2.
   *
   * Gated on `.sspcs-hover`, which the Liquid emits only when the owner's existing
   * "Show second image on hover" setting is on — same switch as before, no new setting.
   *
   * Guards, each for a real failure:
   *  - (hover: hover) and (pointer: fine): on touch, pointerenter fires on tap and would
   *    fight the swipe. Mobile keeps swiping and never sees any of this.
   *  - relatedTarget inside the wrap = a child-to-child move, not a real enter/leave.
   *    pointerleave fires on every child; without this the card snaps back to image 1 while
   *    the pointer is still on it.
   *  - a card the shopper has driven with the dots is marked manual and left alone. Resetting
   *    it on mouse-leave would undo their own navigation.
   */
  var canHover =
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function hoverTarget(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    var wrap = t.closest('.sspcs-wrap');
    if (!wrap) return null;
    if (e.relatedTarget && wrap.contains(e.relatedTarget)) return null; // moving within the card
    return wrap;
  }

  document.addEventListener(
    'pointerenter',
    function (e) {
      var wrap = hoverTarget(e);
      if (!wrap) return;
      var track = wrap.querySelector(SEL);
      if (!track) return;
      hydrateAround(track, indexOf(track)); // prefetch, so the move to image 2 is instant
      if (!canHover || !wrap.classList.contains('sspcs-hover')) return;
      if (wrap.dataset.sspcsManual) return;
      if (indexOf(track) === 0) goTo(track, 1);
    },
    true
  );

  document.addEventListener(
    'pointerleave',
    function (e) {
      if (!canHover) return;
      var wrap = hoverTarget(e);
      if (!wrap || !wrap.classList.contains('sspcs-hover')) return;
      if (wrap.dataset.sspcsManual) return;
      var track = wrap.querySelector(SEL);
      if (track && indexOf(track) !== 0) goTo(track, 0);
    },
    true
  );

  /* Prefetch slide 2 as soon as a card scrolls into view, NOT when the swipe starts —
   * starting the fetch on the swipe means the shopper stares at a blank slide while it
   * downloads (measured: 1.35s on slow 4G). Doing it on approach hides the latency.
   * Scoped by IntersectionObserver + rootMargin so only cards near the viewport pay for it,
   * instead of every card on the page fighting for bandwidth at once. */
  var io = window.IntersectionObserver
    ? new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            var track = entries[i].target;
            hydrateAround(track, indexOf(track));
            io.unobserve(track); // once its first two slides are in, scroll-sync takes over
          }
        },
        { rootMargin: '300px 0px 300px 0px', threshold: 0.01 }
      )
    : null;

  function init(root) {
    var tracks = (root || document).querySelectorAll(SEL);
    for (var i = 0; i < tracks.length; i++) {
      var t = tracks[i];
      if (t.dataset.sspcsInit) continue;
      t.dataset.sspcsInit = '1';
      if (io) io.observe(t);
      else hydrateAround(t, 0);
    }
  }

  /* Extra head start: the instant a finger lands, pull the neighbours in. */
  document.addEventListener(
    'touchstart',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var wrap = t.closest('.sspcs-wrap');
      if (!wrap) return;
      var track = wrap.querySelector(SEL);
      if (track) hydrateAround(track, indexOf(track));
    },
    { capture: true, passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  if (window.MutationObserver) {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.matches && n.matches(SEL) ? true : n.querySelector && n.querySelector(SEL))) {
            init(n.nodeType === 1 ? n : document);
            break;
          }
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
